import { FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FormAlert, FormField, PillButton } from "@NP-Webber/crm-kit";
import { toClientApiError } from "../api/httpClient";
import { useAuth } from "../auth/AuthProvider";
import { postAuthPath } from "../auth/authState";
import "../styles/authTheme.css";

function EyeIcon({ off = false }: { off?: boolean }) {
  if (off) {
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M3 3l18 18M10.58 10.58A2 2 0 0012 14a2 2 0 001.42-.58M9.88 5.09A10.94 10.94 0 0112 5c5.52 0 10 4.48 10 7a10.8 10.8 0 01-2.06 3.17M6.11 6.11A10.94 10.94 0 002 12c0 2.52 4.48 7 10 7 1.74 0 3.37-.36 4.82-1M14.12 14.12A2 2 0 0012 10"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M2 12s4.48-7 10-7 10 7 10 7-10 7-10 7-10-7-10-7z"
        stroke="currentColor"
        strokeWidth="1.75"
      />
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.75" />
    </svg>
  );
}

export function LoginPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !password) {
      setError("יש למלא אימייל וסיסמה");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const next = await auth.login(trimmedEmail, password);
      navigate(postAuthPath(next), { replace: true });
    } catch (err) {
      const apiError = toClientApiError(err);
      if (apiError.code === "INVALID_CREDENTIALS" || apiError.status === 401) {
        setError("אימייל או סיסמה שגויים");
      } else {
        setError(apiError.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <header className="login-topbar">
        <span className="login-topbar__brand">
          <span className="login-topbar__mark">TURBO</span>
          <span className="login-topbar__biz">ביזנעס</span>
        </span>
      </header>

      <div className="login-layout">
        <aside className="login-brand-panel" aria-hidden="true">
          <div className="login-brand-panel__inner">
            <div className="login-brand-panel__lockup">
              <span className="login-brand-panel__mark">TURBO</span>
              <br />
              <span className="login-brand-panel__biz">ביזנעס</span>
            </div>
            {/* <p className="login-brand-panel__tagline">
              פלטפורמה אחת לניהול עסקי
            </p>
            <p className="login-brand-panel__lead">
              התחברו כדי להמשיך לעבודה בארגון שלכם — בבטחה, בעברית, ובשליטה מלאה.
            </p> */}
          </div>
        </aside>

        <main className="login-shell">
          <div className="login-shell__inner">
            <div className="login-card">
              <div className="login-card__head">
                <h1 className="login-card__title">כניסה למערכת</h1>
                <div className="login-card__rule" />
                <p className="login-card__subtitle">
                  הזינו אימייל וסיסמה כדי להמשיך
                </p>
              </div>

              <form className="login-form" onSubmit={handleSubmit} noValidate>
                {error ? <FormAlert severity="error">{error}</FormAlert> : null}

                <FormField
                  label="אימייל"
                  type="email"
                  required
                  value={email}
                  onChange={setEmail}
                  placeholder="name@example.com"
                  inputProps={{
                    autoComplete: "username",
                    dir: "ltr",
                    disabled: loading,
                  }}
                />

                <FormField label="סיסמה" required>
                  <div className="login-password-wrap">
                    <input
                      className="formkit-input"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="current-password"
                      aria-label="סיסמה"
                      disabled={loading}
                      required
                    />
                    <button
                      type="button"
                      className="login-password-toggle"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? "הסתר סיסמה" : "הצג סיסמה"}
                      disabled={loading}
                    >
                      <EyeIcon off={showPassword} />
                    </button>
                  </div>
                </FormField>

                <PillButton
                  type="submit"
                  disabled={loading}
                  loading={loading}
                  sx={{
                    width: "100%",
                    borderRadius: "8px",
                    minHeight: 46,
                    background:
                      "linear-gradient(180deg, #1a2b4a 0%, #0f1c32 100%)",
                  }}
                >
                  {loading ? "מתחבר…" : "התחבר"}
                </PillButton>
              </form>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default LoginPage;
